import React, { useCallback, useEffect, useState } from "react";
import { seoApi } from "@/lib/seoApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Cpu, Plus, Trash2, PlugZap, Save, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  writer: "Writer",
  reviewer: "Reviewer",
  brief_generator: "Brief Generator",
  support: "Support",
  knowledge_processing: "Knowledge Processing",
  internal_linking: "Internal Linking",
  image_generator: "Image Generator",
  embedding_provider: "Embedding Provider",
};

const EMPTY_PROVIDER = {
  id: "", name: "", type: "text", kind: "openai_compatible", family: "",
  base_url: "", api_key: "", server_ip: "", server_domain: "",
  default_model: "", enabled: true, notes: "",
};

function Field({ label, children }) {
  return (
    <label className="block text-xs">
      <span className="text-neutral-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function RoleRow({ role, providers, onSaved }) {
  const [form, setForm] = useState({
    provider_id: role.provider_id || "",
    model: role.model || "",
    temperature: role.temperature ?? 0.7,
    max_tokens: role.max_tokens ?? 0,
    timeout_seconds: role.timeout_seconds ?? 120,
    max_retries: role.max_retries ?? 2,
    enabled: !!role.enabled,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await seoApi.put(`/acf/ai/roles/${role.role}`, {
        provider_id: form.provider_id || null,
        model: form.model,
        temperature: Number(form.temperature),
        max_tokens: Number(form.max_tokens),
        timeout_seconds: Number(form.timeout_seconds),
        max_retries: Number(form.max_retries),
        enabled: form.enabled,
      });
      toast.success(`Role "${ROLE_LABELS[role.role] || role.role}" saved`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-b border-neutral-100" data-testid={`ai-role-row-${role.role}`}>
      <td className="py-2 pr-3 font-medium whitespace-nowrap">
        {ROLE_LABELS[role.role] || role.role}
      </td>
      <td className="py-2 pr-2">
        <select
          className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm bg-white"
          value={form.provider_id}
          onChange={(e) => set("provider_id", e.target.value)}
          data-testid={`ai-role-provider-${role.role}`}
        >
          <option value="">—</option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>{p.id}</option>
          ))}
        </select>
      </td>
      <td className="py-2 pr-2">
        <Input value={form.model} onChange={(e) => set("model", e.target.value)}
               className="h-8 text-sm" data-testid={`ai-role-model-${role.role}`} />
      </td>
      <td className="py-2 pr-2 w-20">
        <Input type="number" step="0.1" value={form.temperature}
               onChange={(e) => set("temperature", e.target.value)} className="h-8 text-sm" />
      </td>
      <td className="py-2 pr-2 w-24">
        <Input type="number" value={form.max_tokens}
               onChange={(e) => set("max_tokens", e.target.value)} className="h-8 text-sm" />
      </td>
      <td className="py-2 pr-2 w-20">
        <Input type="number" value={form.timeout_seconds}
               onChange={(e) => set("timeout_seconds", e.target.value)} className="h-8 text-sm" />
      </td>
      <td className="py-2 pr-2 w-16">
        <Input type="number" value={form.max_retries}
               onChange={(e) => set("max_retries", e.target.value)} className="h-8 text-sm" />
      </td>
      <td className="py-2 pr-2">
        <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)}
                data-testid={`ai-role-enabled-${role.role}`} />
      </td>
      <td className="py-2">
        <Button size="sm" variant="outline" onClick={save} disabled={saving}
                data-testid={`ai-role-save-${role.role}`}>
          <Save size={14} />
        </Button>
      </td>
    </tr>
  );
}

function ProviderForm({ initial, isNew, onDone, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_PROVIDER, ...initial, api_key: "" });
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isNew) return;
    seoApi.get("/acf/ai/providers/catalog")
      .then((r) => setCatalog(r.data.catalog || []))
      .catch(() => {});
  }, [isNew]);

  const applyTemplate = (tpl) => {
    const t = catalog.find((c) => c.template === tpl);
    if (!t) return;
    setForm((f) => ({
      ...f,
      id: t.template,
      name: t.name,
      type: t.type,
      kind: t.kind,
      base_url: t.base_url || "",
      default_model: t.default_model || "",
      notes: t.notes || "",
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await seoApi.post("/acf/ai/providers", form);
      } else {
        const body = { ...form };
        delete body.id;
        if (!body.api_key) delete body.api_key; // keep existing key
        ["api_key_set", "api_key_hint", "builtin", "created_at", "updated_at"].forEach((k) => delete body[k]);
        await seoApi.put(`/acf/ai/providers/${initial.id}`, body);
      }
      toast.success(isNew ? "Provider created" : "Provider updated");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50 space-y-3"
         data-testid="ai-provider-form">
      {isNew && catalog.length > 0 && (
        <Field label="Start from a template (OpenAI, Anthropic, Google AI, OpenRouter, Ollama, vLLM, ComfyUI…)">
          <select className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm bg-white"
                  defaultValue="" onChange={(e) => applyTemplate(e.target.value)}
                  data-testid="provider-form-template">
            <option value="">— custom / no template —</option>
            {catalog.map((c) => (
              <option key={c.template} value={c.template}>{`${c.name} (${c.type})`}</option>
            ))}
          </select>
        </Field>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Field label="ID (slug)">
          <Input value={form.id} disabled={!isNew} data-testid="provider-form-id"
                 onChange={(e) => set("id", e.target.value)} placeholder="my-ai-server" />
        </Field>
        <Field label="Name">
          <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                 data-testid="provider-form-name" />
        </Field>
        <Field label="Type">
          <select className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm bg-white"
                  value={form.type} onChange={(e) => set("type", e.target.value)}
                  data-testid="provider-form-type">
            <option value="text">text</option>
            <option value="image">image</option>
            <option value="embedding">embedding</option>
          </select>
        </Field>
        <Field label="Kind">
          <select className="w-full border border-neutral-200 rounded px-2 py-1.5 text-sm bg-white"
                  value={form.kind} onChange={(e) => set("kind", e.target.value)}
                  data-testid="provider-form-kind">
            <option value="openai_compatible">openai_compatible</option>
            <option value="emergent">emergent</option>
          </select>
        </Field>
        {form.kind === "emergent" && (
          <Field label="Family (anthropic/openai/gemini)">
            <Input value={form.family || ""} onChange={(e) => set("family", e.target.value)} />
          </Field>
        )}
        <Field label="Default model">
          <Input value={form.default_model || ""} onChange={(e) => set("default_model", e.target.value)}
                 data-testid="provider-form-model" />
        </Field>
        <Field label="Base URL">
          <Input value={form.base_url || ""} onChange={(e) => set("base_url", e.target.value)}
                 placeholder="https://ai.example.com/v1" data-testid="provider-form-base-url" />
        </Field>
        <Field label="API Key">
          <Input type="password" value={form.api_key || ""} onChange={(e) => set("api_key", e.target.value)}
                 placeholder={isNew ? "" : "leave empty to keep current"}
                 data-testid="provider-form-api-key" />
        </Field>
        <Field label="Server IP (optional)">
          <Input value={form.server_ip || ""} onChange={(e) => set("server_ip", e.target.value)}
                 placeholder="10.0.0.5:8000" />
        </Field>
        <Field label="Server Domain (optional)">
          <Input value={form.server_domain || ""} onChange={(e) => set("server_domain", e.target.value)}
                 placeholder="ai.mycompany.com" />
        </Field>
        <Field label="Notes">
          <Input value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
        </Field>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} /> Enabled
        </label>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={onCancel}><X size={14} /> Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="provider-form-save">
          <Save size={14} /> {isNew ? "Create" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function ProviderCard({ p, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await seoApi.post(`/acf/ai/providers/${p.id}/test`);
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, detail: e?.response?.data?.detail || String(e) });
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete provider "${p.id}"?`)) return;
    try {
      await seoApi.delete(`/acf/ai/providers/${p.id}`);
      toast.success("Provider deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    }
  };

  if (editing) {
    return <ProviderForm initial={p} isNew={false}
                          onDone={() => { setEditing(false); onChanged(); }}
                          onCancel={() => setEditing(false)} />;
  }

  return (
    <div className="border border-neutral-200 rounded-lg p-4 flex flex-col gap-2"
         data-testid={`ai-provider-card-${p.id}`}>
      <div className="flex items-center gap-2">
        <span className="font-medium">{p.name || p.id}</span>
        <Badge variant="secondary" className="text-xs">{p.type}</Badge>
        <Badge variant="outline" className="text-xs">{p.kind}</Badge>
        {p.builtin && <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">built-in</Badge>}
        {p.enabled
          ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">enabled</Badge>
          : <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">disabled</Badge>}
      </div>
      <div className="text-xs text-neutral-500 space-y-0.5">
        <div>id: <code>{p.id}</code>{p.family ? <> · family: <code>{p.family}</code></> : null}</div>
        {p.default_model && <div>model: <code>{p.default_model}</code></div>}
        {(p.base_url || p.server_domain || p.server_ip) && (
          <div>endpoint: <code>{p.base_url || p.server_domain || p.server_ip}</code></div>
        )}
        <div>api key: {p.api_key_set ? `set (${p.api_key_hint})` : "—"}</div>
      </div>
      {testResult && (
        <div className={`text-xs rounded px-2 py-1.5 ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
             data-testid={`ai-provider-test-result-${p.id}`}>
          {testResult.ok ? "✓ OK" : "✗ FAILED"}
          {testResult.latency_ms != null && ` · ${testResult.latency_ms} ms`}
          {testResult.detail && ` · ${testResult.detail}`}
        </div>
      )}
      <div className="flex gap-2 mt-1">
        <Button size="sm" variant="outline" onClick={test} disabled={testing}
                data-testid={`ai-provider-test-${p.id}`}>
          <PlugZap size={14} /> {testing ? "Testing…" : "Test Connection"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)}
                data-testid={`ai-provider-edit-${p.id}`}>
          <Pencil size={14} /> Edit
        </Button>
        {!p.builtin && (
          <Button size="sm" variant="outline" className="text-red-600" onClick={remove}
                  data-testid={`ai-provider-delete-${p.id}`}>
            <Trash2 size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AISettings() {
  const [roles, setRoles] = useState([]);
  const [providers, setProviders] = useState([]);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        seoApi.get("/acf/ai/roles"),
        seoApi.get("/acf/ai/providers"),
      ]);
      setRoles(r.data.roles || []);
      setProviders(p.data.providers || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="p-8 text-red-600" data-testid="ai-settings-error">{error}</div>;

  return (
    <div className="p-6" data-testid="ai-settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900 flex items-center gap-2">
          <Cpu size={22} /> AI Settings
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Role-based AI Gateway. Roles map business functions to providers/models.
          Changes take effect live (≤15s) — no deploy required.
        </p>
      </div>

      <Card className="mb-6" data-testid="ai-roles-card">
        <CardHeader><CardTitle className="text-base">AI Roles</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-2">Provider</th>
                  <th className="py-2 pr-2">Model</th>
                  <th className="py-2 pr-2">Temp</th>
                  <th className="py-2 pr-2">Max tokens</th>
                  <th className="py-2 pr-2">Timeout (s)</th>
                  <th className="py-2 pr-2">Retries</th>
                  <th className="py-2 pr-2">On</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <RoleRow key={r.role} role={r} providers={providers} onSaved={load} />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="ai-providers-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">AI Providers</CardTitle>
          <Button size="sm" onClick={() => setAdding((v) => !v)} data-testid="ai-provider-add-btn">
            <Plus size={14} /> Add Provider
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {adding && (
            <ProviderForm initial={EMPTY_PROVIDER} isNew
                           onDone={() => { setAdding(false); load(); }}
                           onCancel={() => setAdding(false)} />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {providers.map((p) => (
              <ProviderCard key={p.id} p={p} onChanged={load} />
            ))}
          </div>
          <p className="text-xs text-neutral-500 mt-2">
            openai_compatible providers work with any OpenAI-compatible API:
            your own AI server (vLLM / Ollama / LocalAI), OpenAI Images, FLUX,
            Stable Diffusion or ComfyUI behind an OpenAI-compatible facade.
            Switch a role's provider — no code changes, no deploy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
